import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import RestaurantAccessSettings, { RestaurantAccessContext } from './RestaurantAccessSettings';
import './OperatingGroupsSettings.css';

type Department = {
  id: number;
  name: string;
  activeOperatingGroupId: number | null;
  activeOperatingGroupName: string | null;
  hasOwnershipConflict: boolean;
};
type Warehouse = { id: number; name: string; code: string; isArchived: boolean };
type Group = {
  id: number;
  name: string;
  description: string;
  isArchived: boolean;
  controlledAccess: boolean;
  memberships: Array<{ id: number; departmentId: number; departmentName: string }>;
  warehouses: Array<{ id: number; warehouseId: number; warehouseName: string; warehouseCode: string }>;
};

const todayInKyiv = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const OperatingGroupsSettings: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupDetails, setGroupDetails] = useState({ name: '', description: '' });
  const [departmentIds, setDepartmentIds] = useState<number[]>([]);
  const [warehouseIds, setWarehouseIds] = useState<number[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState(todayInKyiv);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [newWarehouse, setNewWarehouse] = useState({ name: '', code: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemAdministrator, setSystemAdministrator] = useState(false);
  const [allGroupsOverview, setAllGroupsOverview] = useState(false);
  const canEditStructure = canEdit && !allGroupsOverview;

  const selectedGroup = useMemo(
    () => groups.find(group => String(group.id) === selectedGroupId),
    [groups, selectedGroupId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const access = await api.get<RestaurantAccessContext>('/operating-contexts/restaurant-access');
      setSystemAdministrator(access.data.systemAdministrator);
      setAllGroupsOverview(access.data.allGroupsOverview ?? false);
      if (!access.data.systemAdministrator) return;
      const [groupsResponse, departmentsResponse, warehousesResponse] = await Promise.all([
        api.get<Group[]>('/operating-groups'),
        api.get<Department[]>('/operating-groups/departments'),
        api.get<Warehouse[]>('/operating-groups/warehouses'),
      ]);
      setGroups(groupsResponse.data);
      setDepartments(departmentsResponse.data);
      setWarehouses(warehousesResponse.data);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося завантажити структуру та склади.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    setDepartmentIds(selectedGroup?.memberships.map(item => item.departmentId) ?? []);
    setWarehouseIds(selectedGroup?.warehouses.map(item => item.warehouseId) ?? []);
    setGroupDetails({
      name: selectedGroup?.name ?? '',
      description: selectedGroup?.description ?? '',
    });
  }, [selectedGroup]);

  const toggleId = (id: number, current: number[], set: React.Dispatch<React.SetStateAction<number[]>>) => {
    set(current.includes(id) ? current.filter(value => value !== id) : [...current, id]);
  };

  const createGroup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newGroup.name.trim()) return;
    setSaving(true); setError(''); setMessage('');
    try {
      const { data } = await api.post<Group>('/operating-groups', newGroup);
      await load();
      setSelectedGroupId(String(data.id));
      setNewGroup({ name: '', description: '' });
      setMessage('Порожній ресторан створено. Оновіть панель локального доступу, призначте керівника та створіть нові ресурси окремо.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося створити групу.');
    } finally { setSaving(false); }
  };

  const createWarehouse = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newWarehouse.name.trim() || !newWarehouse.code.trim()) return;
    setSaving(true); setError(''); setMessage('');
    try {
      await api.post('/operating-groups/warehouses', newWarehouse);
      await load();
      setNewWarehouse({ name: '', code: '' });
      setMessage('Склад створено. Прив’яжіть його до операційної групи.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося створити склад.');
    } finally { setSaving(false); }
  };

  const updateGroup = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedGroup || !groupDetails.name.trim()) return;
    setSaving(true); setError(''); setMessage('');
    try {
      await api.put(`/operating-groups/${selectedGroup.id}`, groupDetails);
      await load();
      setMessage('Операційну групу оновлено.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося оновити групу.');
    } finally { setSaving(false); }
  };

  const toggleWarehouseArchive = async (warehouse: Warehouse) => {
    const nextArchived = !warehouse.isArchived;
    const action = nextArchived ? 'архівувати' : 'відновити';
    if (!window.confirm(`Підтвердити дію «${action}» для складу «${warehouse.name}»?`)) return;
    setSaving(true); setError(''); setMessage('');
    try {
      await api.put(`/operating-groups/warehouses/${warehouse.id}`, {
        name: warehouse.name,
        isArchived: nextArchived,
      });
      await load();
      setMessage(nextArchived ? 'Склад архівовано; історія призначень збережена.' : 'Склад відновлено.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося змінити стан складу.');
    } finally { setSaving(false); }
  };

  const saveStructure = async () => {
    if (!selectedGroup) return;
    const newAssignments = departments.filter(department =>
      departmentIds.includes(department.id) &&
      department.activeOperatingGroupId == null &&
      !selectedGroup.memberships.some(membership => membership.departmentId === department.id),
    );
    const confirmUnassignedDepartmentAssignment = newAssignments.length > 0;
    if (confirmUnassignedDepartmentAssignment && !window.confirm(
      `Призначити незакріплені підрозділи (${newAssignments.map(item => item.name).join(', ')}) до групи «${selectedGroup.name}» з ${effectiveFrom}? Буде створено нове датоване членство; історичні членства та дані працівників не переміщуються.`,
    )) return;
    setSaving(true); setError(''); setMessage('');
    try {
      await api.post(`/operating-groups/${selectedGroup.id}/configuration`, {
        departmentIds,
        warehouseIds,
        effectiveFrom,
        confirmUnassignedDepartmentAssignment,
      });
      await load();
      setMessage('Структуру збережено з указаною датою початку дії. Історичні документи не змінюються.');
    } catch (requestError: any) {
      await load();
      setError(requestError?.response?.data?.message || 'Не вдалося зберегти структуру.');
    } finally { setSaving(false); }
  };

  if (loading) return <p role="status">Завантаження структури…</p>;
  if (!systemAdministrator) return <RestaurantAccessSettings canEdit={canEdit} />;
  return (
    <section aria-label="Структура підрозділів і склади" className="operating-groups-settings">
      <RestaurantAccessSettings key={groups.length} canEdit={canEditStructure} />
      {allGroupsOverview && <p role="note">
        Огляд «Усі групи» доступний лише для читання. Перемкніть робочий контекст,
        щоб змінювати структуру чи доступ.
      </p>}
      <p>
        Системне адміністрування. Новий ресторан живе незалежно; чужі підрозділи,
        склади й дані не переносяться. Нижче збережено окреме керування legacy-структурою.
      </p>
      {error && <div role="alert" className="operating-groups-message operating-groups-message--error">{error}</div>}
      {message && <div role="status" className="operating-groups-message">{message}</div>}

      <form onSubmit={createGroup} className="operating-groups-create">
        <h3>Новий порожній ресторан</h3>
        <label>Назва<input value={newGroup.name} maxLength={120} disabled={!canEditStructure || saving} onChange={e => setNewGroup({ ...newGroup, name: e.target.value })} /></label>
        <label>Опис<input value={newGroup.description} maxLength={1000} disabled={!canEditStructure || saving} onChange={e => setNewGroup({ ...newGroup, description: e.target.value })} /></label>
        <button type="submit" disabled={!canEditStructure || saving || !newGroup.name.trim()}>Створити групу</button>
      </form>

      <form onSubmit={createWarehouse} className="operating-groups-create">
        <h3>Новий склад</h3>
        <label>Назва<input value={newWarehouse.name} maxLength={160} disabled={!canEditStructure || saving} onChange={e => setNewWarehouse({ ...newWarehouse, name: e.target.value })} /></label>
        <label>Код<input value={newWarehouse.code} maxLength={40} pattern="[A-Za-z0-9_-]{2,40}" disabled={!canEditStructure || saving} onChange={e => setNewWarehouse({ ...newWarehouse, code: e.target.value.toUpperCase() })} /></label>
        <button type="submit" disabled={!canEditStructure || saving || !newWarehouse.name.trim() || newWarehouse.code.trim().length < 2}>Створити склад</button>
      </form>

      <section className="operating-groups-create" aria-label="Довідник складів">
        <h3>Склади</h3>
        {warehouses.map(warehouse => <div className="operating-groups-warehouse-row" key={warehouse.id}>
          <span>{warehouse.name} ({warehouse.code}){warehouse.isArchived ? ' — архів' : ''}</span>
          <button
            type="button"
            disabled={!canEditStructure || saving}
            onClick={() => void toggleWarehouseArchive(warehouse)}
          >
            {warehouse.isArchived ? 'Відновити' : 'Архівувати'}
          </button>
        </div>)}
        {warehouses.length === 0 && <p>Складів ще немає. Створіть склад вище.</p>}
      </section>

      <div className="operating-groups-editor">
        <label>Операційна група
          <select value={selectedGroupId} onChange={e => setSelectedGroupId(e.target.value)} disabled={saving}>
            <option value="">Оберіть групу</option>
            {groups.filter(group => !group.isArchived).map(group => <option key={group.id} value={group.id}>{group.name}</option>)}
          </select>
        </label>
        <label>Структура діє з<input type="date" value={effectiveFrom} min={todayInKyiv()} disabled={!canEditStructure || saving || !selectedGroup} onChange={e => setEffectiveFrom(e.target.value)} /></label>
        {selectedGroup && <>
          <form onSubmit={updateGroup} className="operating-groups-group-details">
            <label>Назва групи<input value={groupDetails.name} maxLength={120} disabled={!canEditStructure || saving} onChange={e => setGroupDetails({ ...groupDetails, name: e.target.value })} /></label>
            <label>Опис<input value={groupDetails.description} maxLength={1000} disabled={!canEditStructure || saving} onChange={e => setGroupDetails({ ...groupDetails, description: e.target.value })} /></label>
            <button type="submit" disabled={!canEditStructure || saving || !groupDetails.name.trim()}>Зберегти назву й опис</button>
          </form>
          {!selectedGroup.controlledAccess && <><fieldset>
            <legend>Підрозділи у групі</legend>
            {departments.map(department => {
              const activeOwnerIsOtherGroup = department.activeOperatingGroupId != null &&
                department.activeOperatingGroupId !== selectedGroup.id;
              const ownerLabel = department.hasOwnershipConflict
                ? 'Конфлікт кількох активних власників — звірте структуру'
                : department.activeOperatingGroupName
                  ? `Активний власник: ${department.activeOperatingGroupName}`
                  : 'Активної групи немає';
              return <label key={department.id} className="operating-groups-check">
                <input
                  type="checkbox"
                  disabled={!canEditStructure || saving || activeOwnerIsOtherGroup || department.hasOwnershipConflict}
                  checked={departmentIds.includes(department.id)}
                  onChange={() => toggleId(department.id, departmentIds, setDepartmentIds)}
                />
                <span>{department.name}<small>{ownerLabel}</small></span>
              </label>;
            })}
          </fieldset>
          <fieldset>
            <legend>Склади, доступні групі</legend>
            {warehouses.filter(warehouse => !warehouse.isArchived).map(warehouse => <label key={warehouse.id} className="operating-groups-check">
              <input type="checkbox" disabled={!canEditStructure || saving} checked={warehouseIds.includes(warehouse.id)} onChange={() => toggleId(warehouse.id, warehouseIds, setWarehouseIds)} />
              {warehouse.name} ({warehouse.code})
            </label>)}
            {warehouses.length === 0 && <p>Складів ще немає. Створіть склад вище.</p>}
          </fieldset>
          <div className="operating-groups-actions">
            <button type="button" onClick={() => void saveStructure()} disabled={!canEditStructure || saving}>Зберегти структуру</button>
          </div>
          <p role="note">
            Архівування груп наразі недоступне: система не може надійно перевірити
            всі історичні та пов’язані бізнес-дані. Поточна відсутність призначень
            не є доказом порожнечі.
          </p>
          </>}
        </>}
      </div>
      {!canEdit && <p>Перегляд доступний; редагування потребує права зміни налаштувань адміністратора.</p>}
      <p role="note">
        Перед об’єднанням запасів проведіть фактичну звірку та документоване
        переміщення. Перед розділенням визначте нові залишки ревізією та
        перемістіть запаси. Налаштування групи саме по собі не рухає товари.
      </p>
      <p role="note">
        Виробничі кухні й склади — окремі ресурси. Кухня використовує підрозділ
        як організаційний контекст; це не створює зв’язку 1:1 зі складом і не
        змінює правила запасів чи доступу.
      </p>
    </section>
  );
};

export default OperatingGroupsSettings;
